import { Button, Card } from '@/components'
import * as Feather from 'react-feather'
import { Text } from '@/components'
import { LoadingAnimation } from '@/components'
import Tabs from '@/components/Tabs'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { AlertMessage } from '@/app/components'
import { ExpirationScheduleEntry } from '@/types/transaction'
import { formatPoints, MIN_REDEMPTION_POINTS } from '@/utils/rewardPoints'
import { formatRewardDate, pointsToDollars } from '../../rewards.utils'

interface RedeemRewardCardProps {
  transactions: any[]
  pageLoading: boolean
  availablePoints: number
  setPointsRedemptionModalOpen: (open: boolean) => void
  /** The soonest expiration within the expiring-soon window */
  nextExpiration?: ExpirationScheduleEntry
}

function ExpiringPointsBanner({
  expiration,
  availablePoints,
}: {
  expiration: ExpirationScheduleEntry
  availablePoints: number
}) {
  return (
    <AlertMessage variant="info" className="w-full" id="expiring-points-banner">
      You have <strong>{formatPoints(expiration.points)}</strong> expiring on{' '}
      <strong>{formatRewardDate(expiration.expires_at)}</strong>
      {availablePoints < MIN_REDEMPTION_POINTS
        ? `. You need ${formatPoints(MIN_REDEMPTION_POINTS)} to redeem.`
        : null}
    </AlertMessage>
  )
}

export default function RedeemRewardCard(props: RedeemRewardCardProps) {
  const showInviteTab = useFeatureFlag({ flag: 'show_rewards_invite_tab' })

  return (
    <Card className="w-full">
      <div className="w-full h-full flex flex-col items-center gap-6">
        <div
          id="points_display"
          className="flex flex-col items-center justify-center"
        >
          <div className="flex flex-col items-center">
            {props.pageLoading ? (
              <LoadingAnimation type="falling" />
            ) : (
              <>
                <Text variant="h3" className="mb-1">
                  Points Balance
                </Text>
                <div className="flex flex-row items-center gap-2">
                  <Text
                    variant="h1"
                    id="available-points"
                    className="text-themed-primary mb-1"
                    data-availablepoints={props.availablePoints}
                  >
                    {formatPoints(props.availablePoints, { unit: false })}
                  </Text>
                </div>
                <Text variant="body4">
                  Redeemable for up to ${pointsToDollars(props.availablePoints)}
                </Text>
              </>
            )}
          </div>
        </div>
        {!props.pageLoading && props.nextExpiration && (
          <ExpiringPointsBanner
            expiration={props.nextExpiration}
            availablePoints={props.availablePoints}
          />
        )}
        <div id="button_container" className="flex flex-row gap-4 w-full">
          <Button
            id="redeem_button"
            className="flex flex-1"
            onClick={() => props.setPointsRedemptionModalOpen(true)}
          >
            <div className="flex flex-row gap-2 items-center">
              <Feather.Award className="w-4 h-4" />
              Redeem
            </div>
          </Button>
          {showInviteTab && (
            <Button
              id="earn_button"
              variant="primary-subtle"
              className="flex flex-1"
              onClick={() => Tabs.setActiveTabIndex('reward-tabs', 0)}
            >
              <div className="flex flex-row gap-2 items-center">
                <Feather.Zap className="w-4 h-4" />
                Earn
              </div>
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}
